/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { IAvaiableCpmOptionList } from "Interfaces/SiteContent/IAvailableCpmOptionList";
import { ICommonDataState } from "Interfaces/SiteContent/ICommonDataState";
import { IReviewStatusOptionList } from "Interfaces/SiteContent/IReviewStatusOptionList";

import { ICommonDataLoadFail, ICommonDataLoadSuccess } from "./CommonDataAction";
import { COMMON_DATA_LOAD_FAIL, COMMON_DATA_LOAD_SUCCESS } from "./CommonDataActionTypes";

/**
 * Common Data Initial State.
 */
const initialState: ICommonDataState = {
  commonDataOperationStatus: OperationStatus.Idle,
  commonDataLoadError: "",
  mvp: {
    reviewStatuses: [],
    reviewStatusOptionList: [],
    availableCpms: [],
    availableCpmOptionList: [],
  },
  mlsa: {
    availableCpms: [],
    availableCpmOptionList: [],
  },
};

/**
 * Updates state of fetch common data success.
 * @param {ICommonDataState} state The common data state.
 * @param {ICommonDataLoadSuccess} action The common data action success.
 * @returns {ICommonDataState} New common data state.
 */
const applyLoadSuccessCommonData = (
  state: ICommonDataState,
  action: ICommonDataLoadSuccess
): ICommonDataState => ({
  ...state,
  commonDataOperationStatus: OperationStatus.Succeeded,
  mvp: {
    reviewStatuses: action.commonData.mvp.reviewStatuses,
    reviewStatusOptionList: action.commonData.mvp.reviewStatuses.map(
      (x) =>
        ({
          key: x.name,
          text: x.name,
        } as IReviewStatusOptionList)
    ),
    availableCpms: action.commonData.mvp.availableCpms,
    availableCpmOptionList: action.commonData.mvp.availableCpms.map(
      (x) =>
        ({
          key: x.email,
          text: x.name,
        } as IAvaiableCpmOptionList)
    ),
  },
  mlsa: {
    availableCpms: action.commonData.mlsa.availableCpms,
    availableCpmOptionList: action.commonData.mlsa.availableCpms.map(
      (x) =>
        ({
          key: x.email,
          text: x.name,
        } as IAvaiableCpmOptionList)
    ),
  },
});

/**
 * Updates state of fetch common data failure.
 * @param {ICommonDataState} state The common data state.
 * @param {ICommonDataLoadFail} action The common data action failure.
 * @returns {ICommonDataState} New common data state.
 */
const applyLoadFailCommonData = (
  state: ICommonDataState,
  action: ICommonDataLoadFail
): ICommonDataState => ({
  ...state,
  commonDataOperationStatus: OperationStatus.Failed,
  commonDataLoadError: action.commonDataLoadError,
});

/**
 * Reducer function for CommonData component.
 * @param {ICommonDataState} state The activity state.
 * @param {ICommonDataActionTypes} action The action type.
 * @returns {IClRegionDataState} The activity state.
 */

function CommonDataReducer(state: ICommonDataState = initialState, action): ICommonDataState {
  switch (action.type) {
    case COMMON_DATA_LOAD_SUCCESS:
      return applyLoadSuccessCommonData(state, action);
    case COMMON_DATA_LOAD_FAIL:
      return applyLoadFailCommonData(state, action);
    default:
      return state;
  }
}

export default CommonDataReducer;
